import { escapeHtml } from "./shared-utils";

const MAX_BAN_REASON_LENGTH = 240;

export function sanitizeBanReason(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.replace(/\s+/gu, " ").trim();
  if (!normalized) return null;
  return escapeHtml(normalized.slice(0, MAX_BAN_REASON_LENGTH));
}

export function getBanReasonFromMetadata(meta: unknown): string | null {
  if (!meta || typeof meta !== "object") return null;
  return sanitizeBanReason((meta as Record<string, unknown>).ban_reason);
}

export function isUserBannedAuthError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const record = error as Record<string, unknown>;
  const code = record.code ?? record.error_code;
  if (code === "user_banned") return true;
  return (
    typeof record.message === "string" &&
    /user is banned/iu.test(record.message)
  );
}

/**
 * Valida de forma segura si una fecha de baneo está vigente.
 * Protege contra strings inválidos que harían que `new Date()` devuelva
 * `Invalid Date` y la comparación `> new Date()` fuese siempre `false`.
 */
export function isBannedNow(bannedUntil: string | null | undefined): boolean {
  if (!bannedUntil) return false;
  const end = new Date(bannedUntil);
  // 2026-10-28 security hardening: fail-closed on unparseable dates — a
  // corrupted/malformed banned_until must not silently lift a ban.
  if (Number.isNaN(end.getTime())) return true;
  return end.getTime() > Date.now();
}

export function buildBannedPath({
  until,
  reason,
}: {
  until?: string | null;
  reason?: string | null;
}): string {
  const params = new URLSearchParams();
  if (until) params.set("until", until);
  const safeReason = sanitizeBanReason(reason);
  if (safeReason) params.set("reason", safeReason);
  const query = params.toString();
  return query ? `/banned?${query}` : "/banned";
}
